import { RpgItemType, RpgProficiency } from "../models/enums";
import { IRpgCharacter } from "../models/IRpgCharacter";
import { IRpgClass } from "../models/IRpgClass";
import { IRpgStat } from "../models/IRpgStat";
import { IRpgItem } from "../models/IRpgItem";
import { IRpgRace } from "../models/IRpgRace";
import { IRpgSkill } from "../models/IRpgSkill";
import { IRpgSaveThrow } from "../models/IRpgSaveThrow";
import { IRpgBackground } from "../models/IRpgBackground";
import { IAppliedBackgroundGrant } from "../models/IAppliedBackgroundGrant";

export interface CharActions {
    applyBackground: (char: IRpgCharacter, bg: IRpgBackground) => void;
    calcArmorClass: (char: IRpgCharacter) => void;
    calcHitPoints: (char: IRpgCharacter, prevConMod?: number) => void;
    calcInitive: (char: IRpgCharacter) => number;
    calcLevel: (char: IRpgCharacter) => number; 
    calcWealth: (char: IRpgCharacter) => void;
    checkClassReqs: (char: IRpgCharacter, cls: IRpgClass) => { valid: boolean; reasons: string[] };
    generateStats: (stats: string[]) => IRpgStat[];
    getProficiencyBonus: (char: IRpgCharacter) => number;
    getRanNum_Dice: (dice: string) => number;
    getRanNum_MinMax: (min: number, max: number) => number;
    getStatBonus: (stat: IRpgStat | null | undefined) => string;
    getStatDisplay: (stat: IRpgStat | null | undefined) => number;
    newChar: () => IRpgCharacter;
    normalizeImageName: (name: string) => string;
    setDefaultStats: (existing?: IRpgStat[]) => IRpgStat[];
    setSkillValues: (char: IRpgCharacter) => void;
    setStatModsForRace: (char: IRpgCharacter, race: IRpgRace) => void;

    fakeBackgroundList: () => IRpgBackground[];
    fakeClassList: () => IRpgClass[];
    fakeRaceList: () => IRpgRace[];
};

const defaultSkillList: string[] = [
    "Acrobatics-Dex",
    "Animal Handling-Wis",
    "Arcana-Int",
    "Athletics-Str",
    "Deception-Cha",
    "History-Int",
    "Insight-Wis",
    "Intimidation-Cha",
    "Investigation-Int",
    "Medicine-Wis",
    "Nature-Int",
    "Perception-Wis",
    "Performance-Cha",
    "Persuasion-Cha",
    "Religion-Int",
    "Sleight of Hand-Dex",
    "Stealth-Dex",
    "Survival-Wis",
]

export const ABILITY_SCORES = [ "Str", "Int", "Dex", "Wis", "Con", "Cha" ] as const;
export type AbilityScoreName = typeof ABILITY_SCORES[number];

// Initial code from working with AI to keep coin Id's "stable" until get the DB id's.
const COIN_IDS = {
    PP: 100001,
    EP: 100002,
    GP: 100003,
    SP: 100004,
    CP: 100005,
} as const;
type CoinName = keyof typeof COIN_IDS;

const isStackable = (item: IRpgItem) => item.IsStackable !== false;

export class RpgRulesService implements CharActions {
    private readonly backgroundTagPrefix = "BG:";

    //private getOrCreateCoin = (char: IRpgCharacter, coinName: string) => {
    private getOrCreateCoin = (char: IRpgCharacter, coinName: CoinName) => {
        const coinId = COIN_IDS[coinName];
        const existingCoin = char.Items.find((item) => item.Id === coinId);
        if (existingCoin) {
            return existingCoin;
        }

        const newCoin: IRpgItem = {
            //Id: char.Items.length + 1,
            Id: coinId,
            Name: coinName,
            Description: `${coinName} coin`,
            IsStackable: true,
            Quantity: 0,
            Value: "1 " + coinName.toLowerCase(),
            Type: RpgItemType.Coin,
        };
        char.Items.push(newCoin);

        return newCoin;
    };

    /*
        * An item is Given to the Character
    */
    private itemGive(character: IRpgCharacter, itemId: number, quantity: number): void {
        if (!Number.isFinite(quantity) || quantity <= 0) {
            return;
        }

        const existingItem = character.Items.find((invItem) => invItem.Id === itemId);
        if (existingItem && isStackable(existingItem)) {
            existingItem.Quantity += quantity;
            return;
        }

        character.Items.push({
            Id: itemId,
            Name: "",               // optional if you denormalize Name into inventory
            Description: "",
            //IsStackable: true,  // can be omited since undefined => stackable by the helper
            Quantity: quantity,
            Value: "",
            Type: RpgItemType.Item,
        });
    }

    /*
        * An item is Taken from the Character (itemTake)
    */
    private itemTake(char: IRpgCharacter, itemId: number, quantity: number): void {
        if (quantity <= 0) {
            return;
        }

        const index = char.Items.findIndex((item) => item.Id === itemId);
        if (index < 0) {
            return;
        }

        const item = char.Items[index];
        item.Quantity -= quantity;
        if (item.Quantity <= 0) {
            char.Items.splice(index, 1);
        }
    }

    private removeBackgroundTags = (char: IRpgCharacter) => {
        if (!char.AppliedTags || char.AppliedTags.length === 0) {
            return;
        }

        char.AppliedTags = char.AppliedTags.filter((tag) => !tag.startsWith(this.backgroundTagPrefix));
    };

    private undoAppliedBackgroundGrant = (char: IRpgCharacter, grant: IAppliedBackgroundGrant) => {
        // 1) Undo skill proficiencies
        if (grant.SkillProficiencies && grant.SkillProficiencies.length > 0) {
            grant.SkillProficiencies.forEach((skillName) => {
                const match = char.Skills.find((skill) => skill.Name === skillName);
                if (!match) {
                    return;
                }

                // Conservative undo: only revert if background was the only source
                if (
                    match.Proficient === RpgProficiency.Proficient &&
                    match.RacialLevel === RpgProficiency.None &&
                    match.ClassLevel === RpgProficiency.None
                ) {
                    match.Proficient = RpgProficiency.None;
                }
            });
        }

        // 2) Undo starting gold
        if (grant.StartingGoldBonus && grant.StartingGoldBonus > 0) {
            this.itemTake(char, COIN_IDS.GP, grant.StartingGoldBonus);
        }

        // 3) Undo bonus languages (remove "Choice" placeholders)
        if (grant.BonusLanguages && grant.BonusLanguages > 0 && char.Languages && char.Languages.length > 0) {
            let remainingToRemove = grant.BonusLanguages;

            for (let index = char.Languages.length - 1; index >= 0 && remainingToRemove > 0; index--) {
                if (char.Languages[index] === "Choice") {
                    char.Languages.splice(index, 1);
                    remainingToRemove--;
                }
            }
        }

        // 4) Undo starting items
        if (grant.StartingItems && grant.StartingItems.length > 0) {
            grant.StartingItems.forEach((itemGrant) => {
                this.itemTake(char, itemGrant.ItemId, itemGrant.Quantity);
            });
        }

        this.setSkillValues(char);
    };

    // --- Exported Methods ---
    applyBackground = (char: IRpgCharacter, bg: IRpgBackground | null) => {
        // Ensure containers exist
        if (!char.AppliedTags) {
            char.AppliedTags = [];
        }
        if (!char.Items) {
            char.Items = [];
        }
        if (!char.Languages) {
            char.Languages = [];
        }

        // === CLEAR BACKGROUND (None selected) ===
        if (bg === null) {
            if (char.AppliedBackgroundGrant) {
                this.undoAppliedBackgroundGrant(char, char.AppliedBackgroundGrant);
            }

            this.removeBackgroundTags(char);

            char.Background = "";
            char.BackgroundId = 0;
            char.AppliedBackgroundGrant = undefined;

            this.setSkillValues(char);
            return;
        }

        const previousGrant = char.AppliedBackgroundGrant;
        const previousBackgroundId = char.BackgroundId;

        const isSwitchingBackground =
            previousGrant !== undefined &&
            previousBackgroundId !== undefined &&
            previousBackgroundId !== 0 &&
            previousBackgroundId !== bg.Id;

        // If switching backgrounds, undo the previous snapshot first, then clear BG tags
        if (isSwitchingBackground && previousGrant) {
            this.undoAppliedBackgroundGrant(char, previousGrant);
            this.removeBackgroundTags(char);
        }

        // Persist selection
        char.Background = bg.Name;
        char.BackgroundId = bg.Id;

        // Snapshot we will store (only include what we actually apply)
        const newGrant: IAppliedBackgroundGrant = {
            BackgroundId: bg.Id,
            SkillProficiencies: bg.SkillProficiencies ? [ ...bg.SkillProficiencies ] : undefined,
            StartingGoldBonus: bg.StartingGoldBonus,
            BonusLanguages: bg.BonusLanguages,
            StartingItems: bg.StartingItems ? bg.StartingItems.map((item) => ({ ItemId: item.ItemId, Quantity: item.Quantity })) : undefined,
        };

        // 1) Skill proficiencies (idempotent)
        if (bg.SkillProficiencies && bg.SkillProficiencies.length > 0) {
            bg.SkillProficiencies.forEach((skillName) => {
                const match = char.Skills.find((skill) => skill.Name === skillName);

                if (match && match.Proficient === RpgProficiency.None) {
                    match.Proficient = RpgProficiency.Proficient;
                }
            });
        }

        // 2) Starting gold bonus (prevent stacking via tag)
        if (bg.StartingGoldBonus && bg.StartingGoldBonus > 0) {
            const tag = `${this.backgroundTagPrefix}${bg.Id}:Gold`;

            if (!char.AppliedTags.includes(tag)) {
                char.AppliedTags.push(tag);

                const gp = this.getOrCreateCoin(char, "GP");
                gp.Quantity += bg.StartingGoldBonus;
            }
        }

        // 3) Bonus languages (prevent stacking via tag)
        if (bg.BonusLanguages && bg.BonusLanguages > 0) {
            const tag = `${this.backgroundTagPrefix}${bg.Id}:Lang`;

            if (!char.AppliedTags.includes(tag)) {
                char.AppliedTags.push(tag);

                for (let languageIndex = 0; languageIndex < bg.BonusLanguages; languageIndex++) {
                    char.Languages.push("Choice");
                }
            }
        }

        // 4) Starting items (prevent stacking via tag)
        if (bg.StartingItems && bg.StartingItems.length > 0) {
            const tag = `${this.backgroundTagPrefix}${bg.Id}:Items`;

            if (!char.AppliedTags.includes(tag)) {
                char.AppliedTags.push(tag);

                bg.StartingItems.forEach((item) => {
                    this.itemGive(char, item.ItemId, item.Quantity);
                });
            }
        }

        // Store snapshot (single background at a time)
        char.AppliedBackgroundGrant = newGrant;

        this.setSkillValues(char);
    };

    calcArmorClass = (char: IRpgCharacter) => {
        let dexMod: number = 0;

        if (char.AbilityScores.find(x => x.Name === "Dex")?.Modifier) {
            dexMod = char.AbilityScores?.find(x => x.Name === "Dex")?.Modifier as number;
        }

        char.ArmorClass = (10 + dexMod);
    };

    calcHitPoints = (char: IRpgCharacter, prevConMod?: number) => {
        let conMod: number = 0;
        if (char.AbilityScores.find(x => x.Name === "Con")?.Modifier) {
            conMod = Number(this.getStatBonus(char.AbilityScores.find(x => x.Name === "Con")));
        }

        // Hit Dice value is set, but HP (and max) has not been calculated yet.
        if (char.HitDice !== "" && char.HitPoints === 0 && char.HitPointsMax === 0) {
            // Level 1 chars get max possible HP.
            let hpIncrease: number = this.getRanNum_Dice(char.HitDice) + conMod;

            if ((char.HitPoints === 0 || char.HitPointsMax === 0) && (char.Xp === 0)) {
                const index = char.HitDice.toLowerCase().indexOf("d");
                hpIncrease = Number(char.HitDice.substring(index + 1)) + conMod;
            }

            char.HitPoints += hpIncrease;
            char.HitPointsMax += hpIncrease;
        } else {
            if (prevConMod) {
                char.HitPoints -= prevConMod;
                char.HitPointsMax -= prevConMod;
            }

            char.HitPoints += conMod;
            char.HitPointsMax += conMod;
        }
    };

    calcInitive = (char: IRpgCharacter) => {
        //  A character’s Initiative score is typically 10 plus all modifiers to the character’s Initiative roll (including their Dexterity modifier and any special modifiers).
        let dexMod: number = 0;

        // Get Dex Mod.
        if (char.AbilityScores.find(x => x.Name === "Dex")?.Modifier) {
            dexMod = char.AbilityScores?.find(x => x.Name === "Dex")?.Modifier as number;
        }

        // TODO: Find any Abilities or Skills or Feats that also mod the roll.

        return (10 + dexMod) ;
    };

    calcLevel = (char: IRpgCharacter) => {
        const xp = char.Xp;

        if (xp < 300) { return 1;
        } else if (xp < 900) { return 2;
        } else if (xp < 2700) { return 3;
        } else if (xp < 6500) { return 4;
        } else if (xp < 14000) { return 5;
        } else if (xp < 23000) { return 6;
        } else if (xp < 34000) { return 7;
        } else if (xp < 48000) { return 8;
        } else if (xp < 64000) { return 9;
        } else if (xp < 85000) { return 10;
        } else if (xp < 100000) { return 11;
        } else if (xp < 120000) { return 12;
        } else if (xp < 140000) { return 13;
        } else if (xp < 165000) { return 14;
        } else if (xp < 195000) { return 15;
        } else if (xp < 225000) { return 16;
        } else if (xp < 265000) { return 17;
        } else if (xp < 305000) { return 18;
        } else if (xp < 355000) { return 19;
        } else { return 20;
        }

        /*
            Proficiency Bonus (0-4=2, 5-8=3, 9-12=4, 13-16=5, 17-20=6)

            Experience Points | Level | Proficiency Bonus
            0 | 1 | +2
            300 | 2 | 2
            900 | 3 | 2
            2700 | 4 | 2
            6500 | 5 | +3
            14000 | 6 | 3
            23000 | 7 | 3
            34000 | 8 | 3
            48000 | 9 | 4
            64000 | 10 | 4
            85000 | 11 | +4
            100000 | 12 | 4
            120000 | 13 | 5
            140000 | 14 | 5
            165000 | 15 | 5
            195000 | 16 | 5
            225000 | 17 | +6
            265000 | 18 | 6
            305000 | 19 | 6
            355000 | 20 | 6
        */
    };

    calcWealth = (char: IRpgCharacter) => {
        /*
            Cleric	5d4 × 10 gp
            Fighter	5d4 × 10 gp
            Rogue	4d4 × 10 gp
            Wizard	4d4 × 10 gp
        */

        let die = "5d4";
        if ((char.Class === "Rogue") || (char.Class === "Wizard")) {
            die = "4d4";
        }

        const wealthGp = (this.getRanNum_Dice(die) * 10);
        // // TODO: tempCoins should probably be a coin constant.
        // // See COIN_IDS and CoinName at the top of this file.
        // const tempCoins = [ "Platinum", "Electrum", "Gold", "Silver", "Copper"];
        // const coinArr: IRpgItem[] = [];
        //
        // tempCoins.forEach((coin) => {
        //     const tempCoin: IRpgItem = {
        //         Id: char.Items.length + coinArr.length,
        //         Name: coin.substring(0,1) + "P",
        //         Description: coin + " Pieces",
        //         Quantity: 0,
        //         Value: "",
        //         Type: RpgItemType.Coin,
        //     };
        //
        //     if (tempCoin.Name === "GP") {
        //         tempCoin.Quantity = wealthGp;
        //     }
        //
        //     coinArr.push(tempCoin);
        // });
        //
        //char.Items = coinArr;

        // Ensure coin rows exist (and are stackable), but do NOT wipe inventory
        //this.getOrCreateCoin(char, "PP");
        //this.getOrCreateCoin(char, "EP");
        const gp = this.getOrCreateCoin(char, "GP");
        //this.getOrCreateCoin(char, "SP");
        //this.getOrCreateCoin(char, "CP");

        gp.Quantity += wealthGp;
    };

    checkClassReqs = (char: IRpgCharacter, cls: IRpgClass): { valid: boolean; reasons: string[] } => {
        const reasons: string[] = [];
        const requirements = cls.MinStatRequirements;

        if (!requirements) {
            return { valid: true, reasons };
        }

        (Object.entries(requirements) as Array<[AbilityScoreName, number | undefined]>).forEach(([statName, required]) => {
            if (required === undefined) {
                return;
            }

            const actual = this.getStatDisplay(char.AbilityScores.find(x => x.Name === statName));
            if (actual < required) {
                reasons.push(`${statName} ${required}+ required (current: ${actual})`);
            }
        });

        return { valid: reasons.length === 0, reasons };
    };

    generateStats = (stats: string[]) => {
        const ret: IRpgStat[] = [];
        // Math To determine an ability modifier = subtract 10 from the ability score and then divide the result by 2 (round down). 
        
        for (let intX = 0; intX < stats.length; intX++) {
            // Roll four 6-sided dice and add the highest three.
            const diceRolls: number[] = [];
            diceRolls.push(this.getRanNum_Dice("d6"));
            diceRolls.push(this.getRanNum_Dice("d6"));
            diceRolls.push(this.getRanNum_Dice("d6"));
            diceRolls.push(this.getRanNum_Dice("d6"));
            // Sort big to small
            diceRolls.sort((a, b) => b - a);

            const statVal = (diceRolls[0] + diceRolls[1] + diceRolls[2]);
            ret.push( { Name: stats[intX], Value: statVal, Modifier: Math.floor((statVal - 10) / 2), RacialModifier: 0 } );
        }

        return ret;
    };

    getProficiencyBonus = (char: IRpgCharacter) => {
        //Proficiency Bonus (0-4=2, 5-8=3, 9-12=4, 13-16=5, 17-20=6)
        if (char.Level <= 4) { return 2;
        } else if (char.Level <= 8) { return 3;
        } else if (char.Level <= 12) { return 4;
        } else if (char.Level <= 16) { return 5;
        } else { return 6;
        }
    };

    /**
     * Roll dice to get a random #. 
     * Handles "d#" and "#d#". 
     * @return {number}
     */
    getRanNum_Dice = (dice: string) => {
        const splitIndex = dice.toLowerCase().indexOf("d");

        if (splitIndex < 0) {
            return 0;
        }
        const countText = dice.substring(0, splitIndex).trim();
        const intFirstNumber = countText ? Number(countText) : 1;
        const intLastNumber = Number(dice.substring(splitIndex + 1).trim());

        if (!Number.isFinite(intFirstNumber) || !Number.isFinite(intLastNumber) || intFirstNumber <= 0 || intLastNumber <= 0) {
            return 0;
        }

        let total = 0;
        for (let rollIndex = 0; rollIndex < intFirstNumber; rollIndex++) {
            total += this.getRanNum_MinMax(1, intLastNumber);
        }
            
        return total;
    };

    getRanNum_MinMax = (min: number, max: number) => {
        const getRanNum_MinMaxRet: number = Math.floor(Math.random() * (max - min + 1)) + (min * 1);

        return getRanNum_MinMaxRet;
    };

    getStatBonus = (stat: IRpgStat | null | undefined) => {
        const value = stat?.Modifier ?? 0;
        return value >= 0 ? `+${value}` : `${value}`;
    };

    getStatDisplay = (stat: IRpgStat | null | undefined) => {
        if (!stat) {
            return 0;
        }

        return (stat.Value ?? 0) + (stat.RacialModifier ?? 0);
    };

    newChar = () => {
        const tempSkills: IRpgSkill[] = [];
        defaultSkillList.forEach((skillEntry) => {
            tempSkills.push({
                Name: skillEntry.split("-")[0],
                Proficient: RpgProficiency.None,
                RacialLevel: RpgProficiency.None,
                ClassLevel: RpgProficiency.None,
                Stat: skillEntry.split("-")[1],
                Value: 0,
            });
        });

        const tempSaves: IRpgSaveThrow[] = [];
        ABILITY_SCORES.forEach((entry) => {
            tempSaves.push({
                Name: entry,
                Proficient: false,
                Value: 0,
            }
            );
        });

        const tempChar: IRpgCharacter =  {
            Traits: [],
            ArmorClass: 10,
            Alignment: "",
            Class: "",
            HitDice: "",
            HitPoints: 0,
            HitPointsMax: 0,
            Items: [],
            Languages: [],
            Level: 0,
            Name: "",
            Saves: tempSaves,
            Skills: tempSkills,
            Race: "",
            Speed: "",
            AbilityScores: [],
            Xp: 0,
            Id: 0,
            UserId: 0
        };
        return tempChar;
    };

    normalizeImageName = (name: string) => {
        return name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");
    };

    setDefaultStats = (existing?: IRpgStat[]): IRpgStat[] => {
        const stats: IRpgStat[] = [];

        ABILITY_SCORES.forEach((name) => {
            const match = existing?.find(x => x.Name === name);

            if (match) {
                stats.push(match);
            } else {
                const value = 0;
                stats.push({
                    Name: name,
                    Value: value,
                    RacialModifier: 0,
                    Modifier: Math.floor((value - 10) / 2),
                });
            }
        });

        return stats;
    }

    setSkillValues = (char: IRpgCharacter) => {
        char.Skills.forEach((charSkill) => {
            let tempVal = 0;

            if (charSkill.Proficient !== RpgProficiency.None) {
                if (charSkill.Proficient === RpgProficiency.Proficient) {
                    // ProficiencyBonus X 1
                    tempVal = (Number(this.getProficiencyBonus(char)) * 1);
                }
                if (charSkill.Proficient === RpgProficiency.Expert) {
                    // ProficiencyBonus X 2
                    tempVal = (Number(this.getProficiencyBonus(char)) * 2);
                }
            }

            if (charSkill.Stat !== "") {
                tempVal += (Number(this.getStatBonus(char.AbilityScores.find(x => x.Name === charSkill.Stat))) * 1);
            }

            charSkill.Value = tempVal;
        });
    }

    setStatModsForRace = (char: IRpgCharacter, race: IRpgRace) => {
        // Clear existing/previous Racial Modifiers.
        if (char.AbilityScores && char.AbilityScores.length > 0 && race.Name !== "") {
            char.AbilityScores.forEach((stat: IRpgStat) => {
                if (stat && stat.RacialModifier !== 0) {
                    stat.RacialModifier = 0;
                    stat.Modifier = Math.floor(((stat.Value) - 10) / 2);
                }
            });
        }

        if (!char.AbilityScores || char.AbilityScores.length === 0) {
            return;
        }

        // Preferred: structured StatMods
        const structuredMods = race.Bonuses?.StatMods;
        if (structuredMods && Object.keys(structuredMods).length > 0) {
            Object.entries(structuredMods).forEach(([statName, raceMod]) => {
                if (raceMod === undefined || raceMod === null) {
                    return;
                }

                const matchIndex = char.AbilityScores.findIndex((x: IRpgStat) => x.Name === statName);
                if (matchIndex < 0) {
                    return;
                }

                char.AbilityScores[matchIndex].RacialModifier = raceMod;
                char.AbilityScores[matchIndex].Modifier = Math.floor(((char.AbilityScores[matchIndex].Value + raceMod) - 10) / 2);
            });

            return;
        }

        // Fallback: legacy string StatMods
        if (race.StatMods !== null && race.StatMods !== undefined && race.StatMods.trim() !== "") {
            let racialMods = race.StatMods;
            // Normalize the data
            if (racialMods.includes(";")) {
                racialMods = racialMods.replaceAll(";", ",");
            }
            if (racialMods.includes(", ")) {
                racialMods = racialMods.replaceAll(", ", ",");
            }
            if (racialMods.includes(" +")) {
                racialMods = racialMods.replaceAll(" +", "+");
            }
            if (racialMods.includes(" -")) {
                racialMods = racialMods.replaceAll(" -", "-");
            }

            // Create an array from the data
            const mods: string[] = racialMods.split(",");

            mods.forEach(mod => {
                mod = mod.trim();
                let stat = "";
                let raceMod: number = 0;

                // Split the mod into the pieces we need.
                if (mod.includes("+")) {
                    const split = mod.split("+");
                    stat = split[0];
                    raceMod = Number("+" + split[split.length - 1]);
                } else if (mod.includes("-")) {
                    const split = mod.split("-");
                    stat = split[0];
                    raceMod = Number("-" + split[split.length - 1]);
                }

                // Check that we have Char stats to update.
                if (char.AbilityScores.find((x) => x.Name === stat)) {
                    const matchIndex = char.AbilityScores.findIndex((x: IRpgStat) => x.Name === stat);
                    char.AbilityScores[matchIndex].RacialModifier = raceMod;
                    char.AbilityScores[matchIndex].Modifier = Math.floor(((char.AbilityScores[matchIndex].Value + raceMod) - 10) / 2);
                }
            });
        }
    };


    fakeBackgroundList = (): IRpgBackground[] => {
        const ret: IRpgBackground[] = [
            {
                Id: 1,
                Name: "Noble",
                Description: "You were raised among wealth and power. People treat you with deference, and you know how to navigate high society.",
                SkillProficiencies: [ "History", "Persuasion" ],
                BonusLanguages: 1,
                StartingGoldBonus: 25,
                Features: [ "Position of Privilege" ],
            },
            {
                Id: 2,
                Name: "Soldier",
                Description: "You served in an organized military force. You understand rank, tactics, and the bonds forged by hardship.",
                SkillProficiencies: [ "Athletics", "Intimidation" ],
                BonusLanguages: 0,
                StartingGoldBonus: 15,
                Features: [ "Military Rank" ],
            },
            {
                Id: 3,
                Name: "Sage",
                Description: "You spent years learning lore, history, and the workings of the world. People seek your insights and expertise.",
                SkillProficiencies: [ "Arcana", "History" ],
                BonusLanguages: 2,
                StartingGoldBonus: 10,
                Features: [ "Researcher" ],
            },
            {
                Id: 4,
                Name: "Outlander",
                Description: "You grew up in the wilds, far from civilization. You can find food and shelter where others would be lost.",
                SkillProficiencies: [ "Survival", "Athletics" ],
                BonusLanguages: 1,
                StartingGoldBonus: 10,
                Features: [ "Wanderer" ],
            },
            {
                Id: 5,
                Name: "Criminal",
                Description: "You have a history of breaking the law. You know the underworld, its codes, and how to disappear when needed.",
                SkillProficiencies: [ "Deception", "Stealth" ],
                BonusLanguages: 0,
                StartingGoldBonus: 20,
                Features: [ "Criminal Contact" ],
            },
            {
                Id: 6,
                Name: "Acolyte",
                Description: "You served in a temple or religious order. You know sacred rites and can find help among the faithful.",
                SkillProficiencies: [ "Insight", "Religion" ],
                BonusLanguages: 2,
                StartingGoldBonus: 10,
                Features: [ "Shelter of the Faithful" ],
            },
        ];

        return ret;
    };

    fakeClassList = (): IRpgClass[] => {
        return [
            {
                Id: 8,
                Name: "Barbarian",
                Active: true,
                Description: "A ferocious warrior who uses fury and instinct to bring down foes.",
                LongDescription: "Barbarians thrive in the chaos of melee combat, channeling rage to overpower their enemies and endure brutal punishment.",
                HitDice: "d12",
                PrimaryStats: [ "Str", "Con" ],
                Role: "Tank / Damage Dealer",
                KeySkills: [ "Athletics", "Intimidation", "Survival" ],
                MinStatRequirements: { Str: 13 },
                Traits: [
                    { Name: "Rage", Description: "Enter a battle frenzy to deal more damage and resist harm." },
                    { Name: "Unarmored Defense", Description: "When not wearing armor, your defenses rely on agility and toughness." },
                ],
            },
            {
                Id: 12,
                Name: "Cleric",
                Active: true,
                Description: "A master of divine magic and a capable warrior as well.",
                LongDescription: "Clerics serve as conduits of divine power, supporting allies with healing and blessings while wielding sacred magic against foes.",
                HitDice: "d8",
                PrimaryStats: [ "Wis", "Con" ],
                Role: "Healer / Support",
                KeySkills: [ "Insight", "Medicine", "Religion" ],
                MinStatRequirements: { Wis: 13 },
                Traits: [
                    { Name: "Spellcasting", Description: "Cast divine spells to heal, protect, and smite." },
                    { Name: "Channel Divinity", Description: "Use divine power for potent effects tied to your faith." },
                ],
            },
            {
                Id: 14,
                Name: "Commoner",
                Active: true,
                Description: "This NPC class contains laborers of the world.",
                LongDescription: "Commoners are ordinary folk—farmhands, artisans, laborers—rarely trained for danger, but sometimes forced into adventure.",
                HitDice: "d6",
                PrimaryStats: [ "Con" ],
                // Role intentionally omitted (optional / undefined)
                KeySkills: [ "Athletics", "Insight", "Survival" ],
                Traits: [
                    { Name: "Everyday Resilience", Description: "You’ve survived hard work and harder days." },
                ],
            },
            {
                Id: 15,
                Name: "Fighter",
                Active: true,
                Description: "Masters of martial combat, Fighters are versatile warriors skilled with weapons and armor.",
                LongDescription: "Fighters are frontline experts trained in a wide range of weapons and tactics, adapting to any battle with skill and grit.",
                HitDice: "d10",
                PrimaryStats: [ "Str", "Dex" ],
                Role: "Tank / Damage Dealer",
                KeySkills: [ "Athletics", "Intimidation", "Survival" ],
                MinStatRequirements: { Str: 13 },
                Traits: [
                    { Name: "Fighting Style", Description: "Adopt a combat specialty that shapes your approach to battle." },
                    { Name: "Second Wind", Description: "Regain a burst of stamina in the thick of combat." },
                    { Name: "Action Surge", Description: "Push beyond normal limits for an extra burst of action." },
                ],
            },
            {
                Id: 16,
                Name: "Rogue",
                Active: true,
                Description: "Cunning and agile, Rogues rely on stealth, precision, and trickery.",
                LongDescription: "Rogues strike from the shadows, exploit openings, and excel at skills that keep them one step ahead of danger.",
                HitDice: "d8",
                PrimaryStats: [ "Dex", "Int" ],
                Role: "Damage Dealer / Utility",
                KeySkills: [ "Stealth", "Sleight of Hand", "Acrobatics" ],
                MinStatRequirements: { Dex: 13 },
                Traits: [
                    { Name: "Sneak Attack", Description: "Deal extra damage when you have the advantage or an ally distracts your target." },
                    { Name: "Cunning Action", Description: "Use quick movement and tactical repositioning in combat." },
                ],
            },
            {
                Id: 17,
                Name: "Wizard",
                Active: true,
                Description: "Scholars of the arcane, Wizards wield magic through study and discipline.",
                LongDescription: "Wizards command a vast spell repertoire, shaping reality with knowledge, preparation, and arcane mastery.",
                HitDice: "d6",
                PrimaryStats: [ "Int" ],
                Role: "Damage Dealer / Control",
                KeySkills: [ "Arcana", "History", "Investigation" ],
                MinStatRequirements: { Int: 13 },
                Traits: [
                    { Name: "Spellcasting", Description: "Prepare and cast a wide variety of arcane spells." },
                    { Name: "Arcane Recovery", Description: "Recover spell power during a short rest." },
                ],
            },
            {
                Id: 18,
                Name: "Ranger",
                Active: true,
                Description: "Hunters and scouts, Rangers thrive at the edges of civilization.",
                LongDescription: "Rangers track foes, survive harsh wilderness, and combine martial skill with nature-born techniques.",
                HitDice: "d10",
                PrimaryStats: [ "Dex", "Wis" ],
                Role: "Damage Dealer / Utility",
                KeySkills: [ "Survival", "Perception", "Stealth" ],
                MinStatRequirements: { Dex: 13 },
                Traits: [
                    { Name: "Favored Enemy", Description: "Gain advantages when hunting chosen foes (future detail)." },
                    { Name: "Natural Explorer", Description: "Move and survive more effectively in familiar terrain (future detail)." },
                ],
            },
        ];
    };

    fakeRaceList = (): IRpgRace[] => {
        const ret: IRpgRace[] = [
            {
                Id: 1,
                Name: "Human",
                Active: true,
                Description: "Versatile and ambitious.",
                LongDescription: "Humans are adaptable and driven, found in every corner of the world. Their ambition and flexibility make them suited to any role.",
                Image: this.normalizeImageName("human"),
                Bonuses: {
                    Traits: [ "Versatile (future)" ],
                    Languages: [ "Common", "Choice (future)" ],
                    Proficiencies: [ "Choice (future)" ],
                },
                // Legacy UI fields (temporary)
                Languages: "Common, Choice",
                Proficiencies: "Choice",
                Speed: "30 ft",
                Size: "Medium",
                StatMods: "",
            },
            {
                Id: 2,
                Name: "Elf",
                Active: true,
                Description: "Graceful and magical.",
                LongDescription: "Elves are long-lived, graceful folk with keen senses and an affinity for magic. They move with effortless poise and see beauty in all things.",
                Image: this.normalizeImageName("elf"),
                Bonuses: {
                    StatMods: { Dex: 2 },
                    Speed: 30,
                    Senses: [ "Darkvision 60 ft" ],
                    Languages: [ "Common", "Elvish" ],
                    Traits: [ "Fey Ancestry", "Trance" ],
                },
                Languages: "Common, Elvish",
                Senses: "Darkvision 60 ft",
                Speed: "30 ft",
                Size: "Medium",
                StatMods: "Dex+2",
                Traits: [ "Fey Ancestry", "Trance" ],
            },
            {
                Id: 3,
                Name: "High Elf",
                Active: true,
                Description: "Keen mind and arcane talent.",
                LongDescription: "High Elves are refined and scholarly, often mastering art, lore, and arcane practice. Their senses are sharp and their traditions ancient.",
                Image: this.normalizeImageName("high elf"),
                Bonuses: {
                    StatMods: { Dex: 2, Int: 1 },
                    Speed: 30,
                    Senses: [ "Darkvision 60 ft" ],
                    Languages: [ "Common", "Elvish", "Choice (future)" ],
                    Traits: [ "Fey Ancestry", "Trance", "Cantrip (future)" ],
                },
                Languages: "Common, Elvish, Choice",
                Senses: "Darkvision 60 ft",
                Speed: "30 ft",
                Size: "Medium",
                StatMods: "Dex+2, Int+1",
                Traits: [ "Fey Ancestry", "Trance" ],
            },
            {
                Id: 4,
                Name: "Wood Elf",
                Active: true,
                Description: "Swift and keen-eyed.",
                LongDescription: "Wood Elves live in harmony with nature, skilled in stealth and archery. Their ancient blood grants them long life and a natural affinity for the wild.",
                Image: this.normalizeImageName("wood elf"),
                Bonuses: {
                    StatMods: { Dex: 2, Wis: 1 },
                    Speed: 35,
                    Senses: [ "Darkvision 60 ft" ],
                    Languages: [ "Common", "Elvish" ],
                    Traits: [ "Fey Ancestry", "Mask of the Wild" ],
                },
                Languages: "Common, Elvish",
                Senses: "Darkvision 60 ft",
                Speed: "35 ft",
                Size: "Medium",
                StatMods: "Dex+2, Wis+1",
                Traits: [ "Mask of the Wild" ],
            },
            {
                Id: 5,
                Name: "Drow",
                Active: true,
                Description: "Dark elves with keen senses.",
                LongDescription: "Drow dwell in the Underdark beneath the world. Their culture is harsh, but they are gifted with sharp senses and innate magic.",
                Image: this.normalizeImageName("drow"),
                Bonuses: {
                    StatMods: { Dex: 2, Cha: 1 },
                    Speed: 30,
                    Senses: [ "Darkvision 120 ft" ],
                    Languages: [ "Common", "Elvish" ],
                    Traits: [ "Fey Ancestry", "Drow Magic (future)" ],
                },
                Negatives: {
                    Traits: [ "Sunlight Sensitivity (future)" ],
                },
                Languages: "Common, Elvish",
                Senses: "Darkvision 120 ft",
                Speed: "30 ft",
                Size: "Medium",
                StatMods: "Dex+2, Cha+1",
                Traits: [ "Sunlight Sensitivity (future)" ],
            },
            {
                Id: 6,
                Name: "Dwarf",
                Active: true,
                Description: "Tough and resilient.",
                LongDescription: "Dwarves are stout and enduring, known for their craftsmanship and grit. They are steady in battle and steadfast in tradition.",
                Image: this.normalizeImageName("dwarf"),
                Bonuses: {
                    StatMods: { Con: 2 },
                    Speed: 25,
                    Senses: [ "Darkvision 60 ft" ],
                    Languages: [ "Common", "Dwarvish" ],
                    Traits: [ "Dwarven Resilience" ],
                },
                Languages: "Common, Dwarvish",
                Senses: "Darkvision 60 ft",
                Speed: "25 ft",
                Size: "Medium",
                StatMods: "Con+2",
                Traits: [ "Dwarven Resilience" ],
            },
            {
                Id: 7,
                Name: "Hill Dwarf",
                Active: true,
                Description: "Hardy and wise.",
                LongDescription: "Hill Dwarves are famously hearty, with a keen intuition and a deep connection to ancestral ways. They endure where others falter.",
                Image: this.normalizeImageName("hill dwarf"),
                Bonuses: {
                    StatMods: { Con: 2, Wis: 1 },
                    Speed: 25,
                    Senses: [ "Darkvision 60 ft" ],
                    Languages: [ "Common", "Dwarvish" ],
                    Traits: [ "Dwarven Resilience", "Dwarven Toughness" ],
                },
                Languages: "Common, Dwarvish",
                Senses: "Darkvision 60 ft",
                Speed: "25 ft",
                Size: "Medium",
                StatMods: "Con+2, Wis+1",
                Traits: [ "Dwarven Toughness" ],
            },
            {
                Id: 8,
                Name: "Mountain Dwarf",
                Active: true,
                Description: "Strong and battle-ready.",
                LongDescription: "Mountain Dwarves are powerful and trained for war. They are known for armorcraft and a direct, unshakable confidence.",
                Image: this.normalizeImageName("mountain dwarf"),
                Bonuses: {
                    StatMods: { Con: 2, Str: 2 },
                    Speed: 25,
                    Senses: [ "Darkvision 60 ft" ],
                    Languages: [ "Common", "Dwarvish" ],
                    Proficiencies: [ "Light Armor (future)", "Medium Armor (future)" ],
                    Traits: [ "Dwarven Resilience" ],
                },
                Languages: "Common, Dwarvish",
                Senses: "Darkvision 60 ft",
                Speed: "25 ft",
                Size: "Medium",
                StatMods: "Con+2, Str+2",
            },
            {
                Id: 9,
                Name: "Halfling",
                Active: true,
                Description: "Nimble and lucky.",
                LongDescription: "Halflings are cheerful, brave, and surprisingly daring. Their luck and quickness help them thrive in dangerous places.",
                Image: this.normalizeImageName("halfling"),
                Bonuses: {
                    StatMods: { Dex: 2 },
                    Speed: 25,
                    Languages: [ "Common", "Halfling" ],
                    Traits: [ "Lucky", "Brave" ],
                },
                Languages: "Common, Halfling",
                Speed: "25 ft",
                Size: "Small",
                StatMods: "Dex+2",
                Traits: [ "Lucky", "Brave" ],
            },
            {
                Id: 10,
                Name: "Lightfoot Halfling",
                Active: true,
                Description: "Friendly and stealthy.",
                LongDescription: "Lightfoot Halflings are sociable and naturally stealthy, often blending into crowds with ease and keeping an optimistic outlook.",
                Image: this.normalizeImageName("lightfoot halfling"),
                Bonuses: {
                    StatMods: { Dex: 2, Cha: 1 },
                    Speed: 25,
                    Languages: [ "Common", "Halfling" ],
                    Traits: [ "Lucky", "Brave", "Naturally Stealthy" ],
                },
                Languages: "Common, Halfling",
                Speed: "25 ft",
                Size: "Small",
                StatMods: "Dex+2, Cha+1",
                Traits: [ "Naturally Stealthy" ],
            },
            {
                Id: 11,
                Name: "Stout Halfling",
                Active: true,
                Description: "Sturdy and resilient.",
                LongDescription: "Stout Halflings are tough for their size, with a deep well of endurance. They are hard to poison and harder to stop.",
                Image: this.normalizeImageName("stout halfling"),
                Bonuses: {
                    StatMods: { Dex: 2, Con: 1 },
                    Speed: 25,
                    Languages: [ "Common", "Halfling" ],
                    Traits: [ "Lucky", "Brave", "Stout Resilience" ],
                },
                Languages: "Common, Halfling",
                Speed: "25 ft",
                Size: "Small",
                StatMods: "Dex+2, Con+1",
                Traits: [ "Stout Resilience" ],
            },
            {
                Id: 12,
                Name: "Gnome",
                Active: true,
                Description: "Clever and curious.",
                LongDescription: "Gnomes are inventive and bright, full of boundless curiosity. Their minds are sharp and their spirits energetic.",
                Image: this.normalizeImageName("gnome"),
                Bonuses: {
                    StatMods: { Int: 2 },
                    Speed: 25,
                    Senses: [ "Darkvision 60 ft" ],
                    Languages: [ "Common", "Gnomish" ],
                    Traits: [ "Gnome Cunning" ],
                },
                Languages: "Common, Gnomish",
                Senses: "Darkvision 60 ft",
                Speed: "25 ft",
                Size: "Small",
                StatMods: "Int+2",
                Traits: [ "Gnome Cunning" ],
            },
            {
                Id: 13,
                Name: "Forest Gnome",
                Active: true,
                Description: "Illusion and woodland wit.",
                LongDescription: "Forest Gnomes are secretive and playful, weaving illusions and slipping unseen through the woods. They often befriend small animals.",
                Image: this.normalizeImageName("forest gnome"),
                Bonuses: {
                    StatMods: { Int: 2, Dex: 1 },
                    Speed: 25,
                    Senses: [ "Darkvision 60 ft" ],
                    Languages: [ "Common", "Gnomish" ],
                    Traits: [ "Gnome Cunning", "Natural Illusionist (future)" ],
                },
                Languages: "Common, Gnomish",
                Senses: "Darkvision 60 ft",
                Speed: "25 ft",
                Size: "Small",
                StatMods: "Int+2, Dex+1",
            },
            {
                Id: 14,
                Name: "Rock Gnome",
                Active: true,
                Description: "Tinkerers and makers.",
                LongDescription: "Rock Gnomes are gifted artisans and tinkerers. They love clever gadgets and practical jokes, and they’re always building something new.",
                Image: this.normalizeImageName("rock gnome"),
                Bonuses: {
                    StatMods: { Int: 2, Con: 1 },
                    Speed: 25,
                    Senses: [ "Darkvision 60 ft" ],
                    Languages: [ "Common", "Gnomish" ],
                    Traits: [ "Gnome Cunning", "Artificer’s Lore (future)" ],
                },
                Languages: "Common, Gnomish",
                Senses: "Darkvision 60 ft",
                Speed: "25 ft",
                Size: "Small",
                StatMods: "Int+2, Con+1",
            },
            {
                Id: 15,
                Name: "Half-Elf",
                Active: true,
                Description: "Charming and adaptable.",
                LongDescription: "Half-Elves combine human ambition with elven grace. They often feel pulled between worlds, yet thrive in both.",
                Image: this.normalizeImageName("half elf"),
                Bonuses: {
                    StatMods: { Cha: 2 },
                    Speed: 30,
                    Senses: [ "Darkvision 60 ft" ],
                    Languages: [ "Common", "Elvish", "Choice (future)" ],
                    Traits: [ "Fey Ancestry", "Ability Score Increase (future)" ],
                },
                Languages: "Common, Elvish, Choice",
                Senses: "Darkvision 60 ft",
                Speed: "30 ft",
                Size: "Medium",
                StatMods: "Cha+2, Choice+1, Choice+1",
            },
            {
                Id: 16,
                Name: "Half-Orc",
                Active: true,
                Description: "Strong and relentless.",
                LongDescription: "Half-Orcs combine human adaptability with orcish strength. They can endure punishing blows and fight on through sheer will.",
                Image: this.normalizeImageName("half orc"),
                Bonuses: {
                    StatMods: { Str: 2, Con: 1 },
                    Speed: 30,
                    Senses: [ "Darkvision 60 ft" ],
                    Languages: [ "Common", "Orc" ],
                    Traits: [ "Relentless Endurance", "Savage Attacks" ],
                },
                Languages: "Common, Orc",
                Senses: "Darkvision 60 ft",
                Speed: "30 ft",
                Size: "Medium",
                StatMods: "Str+2, Con+1",
            },
            {
                Id: 17,
                Name: "Tiefling",
                Active: true,
                Description: "Infernal legacy and charm.",
                LongDescription: "Tieflings bear an infernal heritage that sets them apart. They are resilient and often gifted with strange magic.",
                Image: this.normalizeImageName("tiefling"),
                Bonuses: {
                    StatMods: { Cha: 2, Int: 1 },
                    Speed: 30,
                    Senses: [ "Darkvision 60 ft" ],
                    Languages: [ "Common", "Infernal" ],
                    Traits: [ "Hellish Resistance (future)", "Infernal Legacy (future)" ],
                },
                Languages: "Common, Infernal",
                Senses: "Darkvision 60 ft",
                Speed: "30 ft",
                Size: "Medium",
                StatMods: "Cha+2, Int+1",
            },
            {
                Id: 18,
                Name: "Dragonborn",
                Active: true,
                Description: "Proud draconic lineage.",
                LongDescription: "Dragonborn are proud, honorable, and shaped by draconic ancestry. Their presence is imposing, and their heritage grants unique power.",
                Image: this.normalizeImageName("dragonborn"),
                Bonuses: {
                    StatMods: { Str: 2, Cha: 1 },
                    Speed: 30,
                    Languages: [ "Common", "Draconic" ],
                    Traits: [ "Breath Weapon (future)", "Damage Resistance (future)" ],
                },
                Languages: "Common, Draconic",
                Speed: "30 ft",
                Size: "Medium",
                StatMods: "Str+2, Cha+1",
            },
        ];

        return ret;
    };
};

export const RpgRules = new RpgRulesService();
